import { Stack, Typography } from '@mui/material';
import { format, isAfter, isBefore } from 'date-fns';
import { Funnel } from 'features/co-pilot/co-pilot.types';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import { OptionType } from 'features/ui/base-components/pai-auto-complete';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import PaiDatePicker from 'features/ui/base-components/pai-date-picker';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  linkedinAccounts: LinkedinAccount[];
  linkedinAccountFilter: string[];
  /** Bereits auf die gewählten Accounts eingeengt (siehe `statistics-account-filter`). */
  campaigns: Funnel[];
  campaignFilter: string[];
  fromDate: Date;
  toDate: Date;
  showOverall: boolean;

  handleLinkedinAccountFilterChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleCampaignFilterChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleFromDateChange(date: Date): void;

  handleToDateChange(date: Date): void;

  handleShowOverallChange(event: React.ChangeEvent<HTMLInputElement>): void;

  fetchStatistics(newFunnels?: string[]): () => void;
}

const StatisticsHeader: React.FC<Props> = ({
  linkedinAccounts,
  linkedinAccountFilter,
  campaigns,
  campaignFilter,
  fromDate,
  toDate,
  showOverall,
  handleLinkedinAccountFilterChange,
  handleCampaignFilterChange,
  handleFromDateChange,
  handleToDateChange,
  handleShowOverallChange,
  fetchStatistics,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const campaignOptions: OptionType[] = useMemo(() => campaigns.map((campaign) => ({ label: campaign.name, value: campaign._id })), [campaigns]);

  const linkedinAccountOptions: OptionType[] = useMemo(
    () =>
      linkedinAccounts.map((account) => ({
        label: !_.isNil(account.firstName) && !_.isNil(account.lastName) ? `${account.firstName} ${account.lastName}` : account.userEmail,
        value: account._id,
      })),
    [linkedinAccounts]
  );

  // Ein gewählter Account ohne Kampagnen darf nicht als leere Klappliste enden — sonst ist
  // nicht zu unterscheiden, ob nichts passt oder etwas fehlgeschlagen ist.
  const noCampaignsForAccount = _.isEmpty(campaignOptions) && !_.isEmpty(linkedinAccountFilter);

  const subtitle = useMemo(() => {
    const count = campaignFilter.length;
    const campaignPart = count === 0 ? t('statisticsNoCampaignSelected') : t(count === 1 ? 'statisticsSelectedCampaign' : 'statisticsSelectedCampaigns', { count });
    const timePart = showOverall
      ? t('statisticsEntireRuntime')
      : fromDate && toDate
      ? `${format(fromDate, 'dd.MM.yyyy')} – ${format(toDate, 'dd.MM.yyyy')}`
      : undefined;

    return [campaignPart, timePart].filter(Boolean).join(' · ');
  }, [campaignFilter, fromDate, toDate, showOverall, t]);

  return (
    <div className={classes.statisticsHeader}>
      <Stack direction={'row'} alignItems={'flex-end'} justifyContent={'space-between'} gap={3} flexWrap={'wrap'}>
        <Stack gap={'6px'}>
          <Typography className={classes.titleText}>{t('campaignStatistics')}</Typography>
          <Typography className={classes.subtitleText}>{subtitle}</Typography>
        </Stack>
        <Stack direction={'row'} alignItems={'flex-end'} gap={'12px'} flexWrap={'wrap'}>
          <PaiSelect
            value={linkedinAccountFilter}
            label={t('linkedinAccount')}
            multiple={true}
            onChange={handleLinkedinAccountFilterChange}
            style={{ width: '220px' }}
          >
            <PaiMenuItem value={''} disabled={true}>
              {t('selectLinkedinAccount')}
            </PaiMenuItem>
            {linkedinAccountOptions.map((option) => (
              <PaiMenuItem key={option.value} value={option.value} isActive={linkedinAccountFilter.includes(option.value)}>
                {option.label}
              </PaiMenuItem>
            ))}
          </PaiSelect>
          <PaiSelect value={campaignFilter} label={t('campaigns')} multiple={true} onChange={handleCampaignFilterChange} style={{ width: '220px' }}>
            {noCampaignsForAccount ? (
              <PaiMenuItem value={''} disabled={true}>
                {t('noCampaignsForLinkedinAccount')}
              </PaiMenuItem>
            ) : null}
            {campaignOptions.map((option) => (
              <PaiMenuItem key={option.value} value={option.value} isActive={campaignFilter.includes(option.value)}>
                {option.label}
              </PaiMenuItem>
            ))}
          </PaiSelect>
          <PaiDatePicker
            label={t('fromDate')}
            value={fromDate}
            onChange={handleFromDateChange}
            views={['year', 'month', 'day']}
            maxDate={toDate}
            disabled={showOverall}
            style={{ width: '180px' }}
          />
          <PaiDatePicker
            label={t('toDate')}
            value={toDate}
            onChange={handleToDateChange}
            views={['year', 'month', 'day']}
            minDate={fromDate}
            disabled={showOverall}
            style={{ width: '180px' }}
          />
          <Stack direction={'row'} alignItems={'center'} className={classes.checkbox}>
            <PaiCheckbox label={t('overallCampaignRuntime')} checked={showOverall} onChange={handleShowOverallChange} />
          </Stack>
          <div className={classes.loadButton}>
            <PaiButton
              text={t('loadStatistics')}
              border={'rounded'}
              style={{ height: '40px', padding: '0 22px' }}
              disabled={
                _.isEmpty(campaignFilter) || !fromDate || !toDate || (!showOverall && (isAfter(fromDate, toDate) || isBefore(toDate, fromDate)))
              }
              action={fetchStatistics(undefined)}
            />
          </div>
        </Stack>
      </Stack>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  statisticsHeader: {
    width: '100%',
    background: theme.palette.backgrounds.main_new,
    padding: '28px 40px 20px 40px',
    position: 'sticky',
    top: -16,
    zIndex: 99,
  },
  titleText: {
    fontSize: theme.typography.pxToRem(26),
    fontWeight: 500,
    letterSpacing: '-0.2px',
    lineHeight: 1.2,
  },
  subtitleText: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.medium,
  },
  checkbox: {
    height: '40px',
  },
  loadButton: {
    display: 'flex',
    alignItems: 'center',
    height: '40px',
  },
}));

export default StatisticsHeader;
